import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Zap, CheckCircle2, Loader2, CreditCard, Banknote, Building2, Receipt, X, Gift, Copy, MapPin, Home, ChevronLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Admin Quick-Pay PWA (/quick-order).
// Single-screen order builder for staff:
//   1. pick product (with confirmation popup)
//   2. pick payment
//   3. pick shipping (auto-free pickup ≥ 380 ₪)
//   4. customer name + address row + phone
//   5. coupon
//   6. submit
// Credit-card  → redirects to CardCom LowProfile.
// Bit          → shows manual transfer screen with our Bit phone & amount.
// Bank         → shows manual bank-transfer screen with Mizrahi account info.
// Cash         → just creates the order and confirms.

const PAYMENT_METHODS = [
  { id: 'credit_card',   label: 'אשראי',    icon: CreditCard, accent: 'from-blue-500 to-blue-700' },
  { id: 'cash',          label: 'מזומן',    icon: Banknote,    accent: 'from-green-500 to-green-700' },
  { id: 'bit',           label: 'Bit',      icon: Receipt,     accent: 'from-cyan-500 to-cyan-700' },
  { id: 'bank_transfer', label: 'בנקאית',   icon: Building2,   accent: 'from-indigo-500 to-indigo-700' },
];

// Shipping options + cost. Self-pickup removed per user spec (June 2026).
// Pickup is auto-routed (Zigzag-backed) but labelled simply as "נקודת איסוף".
const SHIPPING_OPTIONS = [
  { id: 'delivery', label: 'משלוח עד הבית', cost: 49 },
  { id: 'pickup',   label: 'נקודת איסוף HFD', cost: 17 },
];

const FREE_PICKUP_THRESHOLD = 380; // subtotal ≥ this → pickup tiers free

// Static account info shown on the Bit/Bank confirmation screens. Hard-coded
// per user spec — if these ever change, update here + in any other place
// they appear (none currently). Images served from /public/products so
// iOS Safari (which blocks the customer-assets CDN sometimes) renders
// them reliably on mobile too.
const BIT_PHONE = '0504944192';
const BANK_INFO = {
  name: 'מזרחי טפחות',
  branch: '511',
  account: '339561',
  bankCode: '20',
  beneficiary: 'בריאות הגבר',
  logoUrl: '/products/mizrahi-logo.png',
};
const BIT_LOGO = '/products/bit-logo.png';
const BRAND_LOGO = '/products/logo.png';

// Image overrides keyed by product name (substring match). The DB on
// production still points to external URLs that iOS Safari sometimes
// fails to render — so we resolve to a local PNG when the product name
// matches, regardless of what `image`/`image_url` says.
const LOCAL_PRODUCT_IMAGES = [
  { match: /Back 2 Game/i,       url: '/products/back2game.png' },
  { match: /5 ?IN ?1/i,          url: '/products/5in1.png' },
  { match: /Promescent|השהייה/i, url: '/products/promescent.png' },
  { match: /Titan|טיטן/i,        url: '/products/titan.png' },
  { match: /Vitamale|ויטמל/i,    url: '/products/vitamale.png' },
];

// Pre-purchase info popups (per user spec, June 2026). Shown when the user
// selects a shipping option so they understand timing / location.
const SHIPPING_INFO_POPUPS = {
  delivery: {
    icon: 'home',
    title: 'משלוח עד הבית',
    body: 'המשלוח יגיע לכתובת בשעות הבוקר/צהריים 08:00-16:00 — לכן כדאי להשים כתובת שאתה בשעות האלו נמצא: בית או עבודה.',
  },
  pickup_zigzag: {
    icon: 'pin',
    title: 'נקודת איסוף',
    body: 'לפי הכתובת המלאה שנתת, המערכת תזהה את הנקודה הקרובה והפנויה ביותר ותשלח אליה. תקבל SMS תוך 2-5 ימי עסקים שזה הגיע לשם :)',
  },
  pickup: {
    icon: 'pin',
    title: 'נקודת איסוף HFD',
    body: 'בוחרים נקודת איסוף ספציפית מרשימת הנקודות של HFD — חפשו לפי עיר ובחרו את הנקודה הנוחה ללקוח. תקבל SMS כשהחבילה מגיעה לנקודה.',
  },
};

const QuickOrder = () => {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState({}); // { product_id: quantity }
  const [payment, setPayment] = useState('credit_card');
  const [shipping, setShipping] = useState('delivery');
  const [form, setForm] = useState({ customer_name: '', customer_phone: '', street: '', house_number: '', city: '', notes: '' });
  const [coupon, setCoupon] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [productPopup, setProductPopup] = useState(null);   // shows the product card when admin adds
  const [bitScreen, setBitScreen] = useState(null);          // { amount, order_id, customer_name }
  const [bankScreen, setBankScreen] = useState(null);        // same shape
  const [bitConfirmed, setBitConfirmed] = useState(false);
  // Same "ביצעתי העברה" flow for the bank-transfer screen.
  const [bankConfirmed, setBankConfirmed] = useState(false);
  // Per-shipping-method info popup. When the user picks an option we
  // show a quick popup explaining timing/location, with a "המשך"
  // button. Selection only "commits" after they hit continue, so they
  // can cancel and pick another tier.
  const [shippingPopup, setShippingPopup] = useState(null); // {id, label, body}
  // HFD pickup-point picker: full national list fetched once (cached
  // server-side), filtered locally by the search box.
  const [hfdPoints, setHfdPoints] = useState([]);
  const [hfdSearch, setHfdSearch] = useState('');   // city
  const [hfdStreet, setHfdStreet] = useState('');   // street (optional)
  const [hfdPoint, setHfdPoint] = useState(null); // selected point object
  const [hfdLoading, setHfdLoading] = useState(false);
  // After a cash order is created we let the operator immediately dispatch:
  // send a WhatsApp confirmation to the customer, assign to a team member
  // (ברי / לירן / יניב / דניאל) which also prints the label, or just print
  // without assigning. `dispatchLoading` shows a spinner on whichever
  // action is currently firing.
  const [dispatchLoading, setDispatchLoading] = useState(null);
  const [assignedTo, setAssignedTo] = useState(null); // team member name once assigned
  // Regular customers receive this URL too, so the customer summary + team
  // dispatch actions are hidden behind an admin-only "מערכת" toggle. Any
  // customer sees only the plain success confirmation; the operator taps
  // the small "מערכת" button to reveal the dispatch panel.
  const [showSystemPanel, setShowSystemPanel] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data || [])).catch(() => {});
  }, []);

  // When cash is picked, the only valid shipping is home delivery (COD).
  // If the user had selected a pickup option, snap it back automatically.
  useEffect(() => {
    if (payment === 'cash' && shipping !== 'delivery') setShipping('delivery');
  }, [payment, shipping]);

  // Lazy-fetch all HFD points the first time the HFD option is picked.
  useEffect(() => {
    if (shipping !== 'pickup' || hfdPoints.length > 0 || hfdLoading) return;
    setHfdLoading(true);
    axios.post(`${API}/hfd/pickup-points-all`)
      .then(r => setHfdPoints(r.data || []))
      .catch(() => toast.error('שגיאה בטעינת נקודות HFD'))
      .finally(() => setHfdLoading(false));
  }, [shipping, hfdPoints.length, hfdLoading]);

  // Adding a product opens a confirmation popup with the product image + a
  // little gift-text banner ("מתנה 90+90"). Confirming actually commits the
  // qty change so the cart stays clean if the admin clicks the wrong row.
  const handleProductClick = (product, delta) => {
    if (delta < 0) {
      // Decrementing — apply immediately, no confirmation.
      setSelected(prev => {
        const cur = prev[product.id] || 0;
        const next = Math.max(0, cur + delta);
        const out = { ...prev };
        if (next === 0) delete out[product.id]; else out[product.id] = next;
        return out;
      });
    } else {
      setProductPopup(product);
    }
  };

  const confirmAddProduct = () => {
    if (!productPopup) return;
    setSelected(prev => ({ ...prev, [productPopup.id]: (prev[productPopup.id] || 0) + 1 }));
    setProductPopup(null);
  };

  const items = Object.entries(selected).map(([pid, qty]) => {
    const p = products.find(pr => pr.id === pid);
    if (!p) return null;
    return { product_id: pid, product_name: p.name, quantity: qty, price: p.price };
  }).filter(Boolean);
  // Helper — products API returns the photo under `image` (not
  // `image_url`); fall back to either. If the product name matches a
  // known local PNG we use that — this is the iOS-Safari workaround
  // (external CDN was unreliable on mobile).
  const photoOf = (p) => {
    if (!p) return '';
    const local = LOCAL_PRODUCT_IMAGES.find(m => m.match.test(p.name || ''));
    return local?.url || p.image || p.image_url || '';
  };
  const SHIPPING_ETA = 'משלוח יגיע בין 2-5 ימי עסקים';
  const subtotal = items.reduce((s, it) => s + it.price * it.quantity, 0);
  // Coupon — parses the raw input as a number. If it's a valid positive
  // integer (e.g. "40"), it's treated as a flat NIS discount. Strings
  // like "FREESHIP" are kept as a noted promo (no discount applied,
  // they just get stored on the order's `notes` field).
  const couponNum = parseInt(coupon.replace(/[^0-9]/g, ''), 10);
  const couponDiscount = Number.isFinite(couponNum) && couponNum > 0 ? Math.min(couponNum, subtotal) : 0;
  // Cash → only "עד הבית" is offered (couriers handle COD only on home delivery).
  const effectiveShippingOptions = payment === 'cash'
    ? SHIPPING_OPTIONS.filter(s => s.id === 'delivery')
    : SHIPPING_OPTIONS;
  // Pickup-free above threshold — matches Cart.js logic.
  const pickupFree = (shipping === 'pickup_zigzag' || shipping === 'pickup') && subtotal >= FREE_PICKUP_THRESHOLD;
  const rawShipCost = effectiveShippingOptions.find(s => s.id === shipping)?.cost ?? SHIPPING_OPTIONS.find(s => s.id === shipping)?.cost ?? 0;
  const shipCost = pickupFree ? 0 : rawShipCost;
  const total = Math.max(0, subtotal - couponDiscount + shipCost);
  // HFD pickup → the point address replaces the customer address fields.
  const addressOk = shipping === 'pickup' ? !!hfdPoint : (form.street.trim() && form.city.trim());
  const canSubmit = items.length > 0 && form.customer_name.trim() && form.customer_phone.trim() && addressOk && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error(shipping === 'pickup' && !hfdPoint ? 'נא לבחור נקודת איסוף HFD' : 'חסרים פרטים — בחר מוצר ומלא שם / טלפון / כתובת');
      return;
    }
    setSubmitting(true);
    try {
      const customer_address = shipping === 'pickup' && hfdPoint
        ? `${[hfdPoint.street, hfdPoint.house].filter(Boolean).join(' ')}, ${hfdPoint.city || ''}`.replace(/^, /, '').trim()
        : `${form.street} ${form.house_number}, ${form.city}`.trim();
      const orderPayload = {
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_email: '',
        customer_address,
        items,
        total_amount: total,
        payment_method: payment,
        shipping_method: shipping,
        shipping_cost: shipCost,
        notes: [coupon ? `קופון: ${coupon}` : null, form.notes].filter(Boolean).join(' · '),
      };
      if (shipping === 'pickup' && hfdPoint) {
        orderPayload.pickup_point_id = String(hfdPoint.spotId);
        orderPayload.pickup_point_name = `${hfdPoint.spotName}, ${hfdPoint.city}`;
        orderPayload.pickup_provider = 'hfd';
        orderPayload.hfd_spot_id = String(hfdPoint.spotId);
        orderPayload.city = hfdPoint.city || '';
      }
      const orderRes = await axios.post(`${API}/orders`, orderPayload);
      const orderId = orderRes.data?.id;

      if (payment === 'credit_card') {
        const payRes = await axios.post(`${API}/payments/cardcom/create`, {
          order_id: orderId,
          amount: total,
          product_name: items.map(i => `${i.product_name} × ${i.quantity}`).join(', ').slice(0, 50),
          customer_name: form.customer_name,
          customer_phone: form.customer_phone,
          customer_email: '',
          customer_address,
          success_url: `${window.location.origin}/cart?payment=success`,
          failure_url: `${window.location.origin}/cart?payment=failed`,
          webhook_url: `${API}/payments/cardcom/webhook`,
        });
        const url = payRes.data?.payment_url;
        if (url) { window.location.href = url; return; }
        toast.error('נכשל ביצירת לינק תשלום');
      } else if (payment === 'bit') {
        setBitScreen({ amount: total, order_id: orderId, customer_name: form.customer_name });
      } else if (payment === 'bank_transfer') {
        setBankScreen({ amount: total, order_id: orderId, customer_name: form.customer_name });
      } else {
        setSuccess(orderRes.data);
        if (navigator.vibrate) navigator.vibrate([40, 30, 40]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'שגיאה בשליחה');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSuccess(null);
    setBitScreen(null);
    setBankScreen(null);
    setBitConfirmed(false);
    setBankConfirmed(false);
    setSelected({});
    setForm({ customer_name: '', customer_phone: '', street: '', house_number: '', city: '', notes: '' });
    setCoupon('');
    setPayment('credit_card');
    setShipping('delivery');
    setHfdPoint(null);
    setHfdSearch('');
    setAssignedTo(null);
    setShowSystemPanel(false);
  };

  // ─── Post-order dispatch helpers ────────────────────────────────
  // Same phone book as the admin dashboard so the WhatsApp we auto-open
  // to the team member matches what /bari does.
  const STAFF_PHONES = { 'ברי': '0539406051', 'לירן': '0509959513', 'יניב': '0515112205', 'דניאל': '0532260855' };

  const sendShippingWhatsApp = () => {
    const rawPhone = (form.customer_phone || '').replace(/[-\s]/g, '');
    if (!rawPhone) { toast.error('אין טלפון ללקוח'); return; }
    const intl = `972${rawPhone.replace(/^0/, '')}`;
    const name = form.customer_name || 'לקוח';
    const totalStr = total.toLocaleString();
    const addr = `${form.street} ${form.house_number}, ${form.city}`.trim() || 'הכתובת שנרשמה בהזמנה';
    const msg = `נא לשים לב!\n${name} היקר!\n\nאנו משלמים 29 ש״ח עבורך על המשלוח מראש.\n\nבסה"כ של: ${totalStr} ש"ח\n\nהמשלוח יגיע עד 2-5 ימי עסקים לכתובת: ${addr}\n\nאם תסרב לקבל את המשלוח או לא תהיה בכתובת והמשלוח יחזור.\n\nאנחנו נצטרך לגבות ממך 29 ש"ח\n\nמאשר?`;
    // Force the direct-chat URI scheme. `wa.me/...` on Windows sometimes
    // triggers the "send to contact" picker in WhatsApp Desktop; the
    // `whatsapp://send?phone=` scheme always opens the chat with the given
    // number even if it's not saved in the contact list.
    const waUrl = `whatsapp://send?phone=${intl}&text=${encodeURIComponent(msg)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  };

  const printLabel = () => {
    if (!success) return;
    // Match the admin dashboard's professional label design (100x150mm
    // thermal print with header, details, payment banner, QR codes for
    // Waze + WhatsApp, and order id footer). Shares the same visual
    // language so a customer receiving a package printed from /bari or
    // /quick-order sees the same thing.
    const isCash = success.payment_method === 'cash';
    const paymentText = ({ cash: 'מזומן', credit_card: 'כרטיס אשראי', bit: 'ביט', bank_transfer: 'העברה בנקאית' })[success.payment_method] || success.payment_method;
    const itemsCount = (success.items || []).reduce((s, i) => s + (i.quantity || 1), 0);
    const amount = total;
    const paymentBanner = isCash
      ? `<div class="pay-banner pay-due"><div class="pay-title">יש לשלם</div><div class="pay-amount">${amount} ש"ח</div></div>`
      : `<div class="pay-banner pay-paid"><div class="pay-title">שולם · ${paymentText}</div><div class="pay-amount">${amount} ש"ח</div></div>`;
    const phoneForWa = `972${(form.customer_phone || '').replace(/^0/, '').replace(/[-\s]/g, '')}`;
    const waMsg = 'שלום, זה השליח, אתה נמצא בכתובת במהלך היום?';
    const waUrl = `whatsapp://send?phone=${phoneForWa}&text=${encodeURIComponent(waMsg)}`;
    const waQr = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=4&data=${encodeURIComponent(waUrl)}`;
    const addr = `${form.street} ${form.house_number}, ${form.city}`.trim();
    const wazeUrl = `https://waze.com/ul?q=${encodeURIComponent(addr)}&navigate=yes`;
    const wazeQr = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=4&data=${encodeURIComponent(wazeUrl)}`;
    const orderShortId = (success.id || '').slice(0, 8);
    const html = `<!DOCTYPE html><html dir="rtl" lang="he"><head><meta charset="UTF-8"><title>מדבקת משלוח</title><style>
      @page { size: 100mm 150mm; margin: 0; }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { width: 100mm; height: 150mm; font-family: Arial, sans-serif; padding: 4mm; display: flex; flex-direction: column; direction: rtl; }
      .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 2mm; margin-bottom: 3mm; font-size: 11pt; font-weight: bold; }
      .details { font-size: 11pt; line-height: 1.8; margin-bottom: 3mm; }
      .row { display: flex; gap: 2mm; }
      .label { font-weight: bold; min-width: 16mm; }
      .pay-banner { text-align: center; padding: 3mm; margin: 2mm 0 3mm 0; border-radius: 2mm; border: 2px solid; }
      .pay-due { background: #fff1f0; border-color: #dc2626; color: #991b1b; }
      .pay-paid { background: #ecfdf5; border-color: #16a34a; color: #166534; }
      .pay-title { font-size: 14pt; font-weight: 900; letter-spacing: 0.5mm; }
      .pay-amount { font-size: 18pt; font-weight: 900; margin-top: 1mm; }
      .qr-wrap { display: flex; justify-content: space-between; gap: 8mm; margin-top: auto; padding: 0 2mm; }
      .qr-item { flex: 1; text-align: center; border: 1px solid #ddd; border-radius: 2mm; padding: 2mm; }
      .qr-item img { width: 32mm; height: 32mm; display: block; margin: 0 auto; }
      .qr-item .qr-label { font-size: 8pt; font-weight: bold; margin-top: 1mm; }
      .footer { border-top: 1px solid #ccc; padding-top: 2mm; margin-top: 2mm; text-align: center; font-size: 8pt; color: #666; }
    </style></head><body>
      <div class="header">משלוח · בריאות הגבר</div>
      <div class="details">
        <div class="row"><span class="label">שם:</span><span>${form.customer_name || ''}</span></div>
        <div class="row"><span class="label">טלפון:</span><span dir="ltr">${form.customer_phone || ''}</span></div>
        <div class="row"><span class="label">כתובת:</span><span>${addr || '-'}</span></div>
        <div class="row"><span class="label">כמות:</span><span>${itemsCount} פריטים</span></div>
        ${form.notes ? `<div class="row"><span class="label">הערות:</span><span>${form.notes}</span></div>` : ''}
      </div>
      ${paymentBanner}
      <div class="qr-wrap">
        <div class="qr-item"><img src="${waQr}" alt="WhatsApp QR" /><div class="qr-label">וואטסאפ ללקוח</div></div>
        <div class="qr-item"><img src="${wazeQr}" alt="Waze QR" /><div class="qr-label">Waze לכתובת</div></div>
      </div>
      <div class="footer">#${orderShortId}</div>
    </body></html>`;
    const printWin = window.open('', '_blank', 'width=420,height=620');
    if (!printWin) { toast.error('החלון הקופץ נחסם. אשר חלונות קופצים לאתר.'); return; }
    printWin.document.write(html);
    printWin.document.close();
    printWin.focus();
    printWin.onload = () => { setTimeout(() => printWin.print(), 500); };
  };

  const assignToTeam = async (sentBy) => {
    if (!success?.id) { toast.error('אין ID להזמנה'); return; }
    setDispatchLoading(sentBy);
    printLabel();
    try {
      const resp = await axios.post(`${API}/admin/orders/${success.id}/mark-private`, { sent_by: sentBy });
      if (resp.data?.success) {
        toast.success(`ההזמנה סומנה כנשלח ע"י ${sentBy}`);
        setAssignedTo(sentBy);
        const phone = STAFF_PHONES[sentBy];
        if (phone) {
          const intl = `972${phone.replace(/^0/, '')}`;
          const msg = `שלום, ${sentBy}\nהקמתי לך משלוח במערכת\n\nבהצלחה ויום טוב`;
          window.open(`whatsapp://send?phone=${intl}&text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
        }
      } else {
        toast.error('שיוך נכשל');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שיוך נכשל');
    } finally {
      setDispatchLoading(null);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('הועתק');
  };

  // ─────────────────────────── Render ───────────────────────────
  return (
    <div className="min-h-screen text-white overflow-x-hidden" dir="rtl" style={{
      background: 'radial-gradient(circle at top right, #332611 0%, #1e1409 60%, #151009 100%)',
      fontFamily: 'Heebo, system-ui, sans-serif',
    }} data-testid="quick-order-page">
      <Toaster richColors position="top-center" />
      <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.07]" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='3'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 0.5  0 0 0 0 0.05  0 0 0 0 0.8'/></filter><rect width='100' height='100' filter='url(%23n)'/></svg>\")",
      }} />

      <div className="relative z-10 max-w-md mx-auto px-4 pt-6 pb-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <img src={BRAND_LOGO} alt="בריאות הגבר" className="w-14 h-14 rounded-2xl object-contain bg-gradient-to-br from-amber-400 to-orange-600 p-1 shadow-lg shadow-orange-500/30" />
          <div>
            <h1 className="text-2xl font-black tracking-tight">תשלום מהיר</h1>
            <p className="text-[12px] text-amber-300/80 font-bold">בריאות הגבר</p>
          </div>
        </div>

        {/* SUCCESS (cash / done) — after a cash order we also let the
            operator dispatch immediately: WhatsApp the customer, assign
            to a team member (which prints a label + WA's the team member),
            or just print. Same behaviour as the /bari admin row actions
            so a phone-order operator has one screen to wrap everything up. */}
        {success && (
          <div className="bg-gradient-to-br from-emerald-900/40 to-emerald-950/60 border border-emerald-500/30 rounded-3xl p-6 text-center backdrop-blur-xl space-y-4" data-testid="qo-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
            <div>
              <h2 className="text-2xl font-black mb-1">הזמנה נוצרה!</h2>
              <p className="text-emerald-200/80 text-sm">ההזמנה נוספה לדאשבורד · {total.toLocaleString()} ₪</p>
              <p className="text-amber-300 text-sm font-bold mt-1">🚚 {SHIPPING_ETA}</p>
            </div>

            {/* Customer summary + dispatch actions are ADMIN-ONLY. A
                regular customer receiving the link just sees the plain
                "הזמנה נוצרה!" confirmation + "הזמנה חדשה" button. The
                operator taps "מערכת" to unlock the dispatch panel. */}
            {showSystemPanel && (<>
            {/* Customer summary card */}
            <div className="bg-black/30 rounded-2xl p-3 text-right text-sm space-y-1 border border-emerald-500/20" data-testid="qo-system-panel">
              <div className="flex justify-between"><span className="text-stone-400">לקוח</span><strong className="text-white">{form.customer_name}</strong></div>
              <div className="flex justify-between"><span className="text-stone-400">טלפון</span><strong className="text-white font-mono" dir="ltr">{form.customer_phone}</strong></div>
              <div className="flex justify-between"><span className="text-stone-400">כתובת</span><strong className="text-white text-[13px]">{`${form.street} ${form.house_number}, ${form.city}`.trim()}</strong></div>
            </div>

            {/* Dispatch actions */}
            <div className="space-y-2">
              {/* Send WhatsApp shipping-confirmation to CUSTOMER */}
              <button
                onClick={sendShippingWhatsApp}
                className="w-full py-3 rounded-2xl bg-green-500 hover:bg-green-600 font-black transition-colors flex items-center justify-center gap-2"
                data-testid="qo-wa-customer"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                שלח הודעת אישור ללקוח
              </button>

              {/* Team member assignment grid */}
              <div className="pt-2">
                <p className="text-[11px] text-stone-400 mb-2">
                  {assignedTo ? <>נשלח ע&quot;י <strong className="text-emerald-300">{assignedTo}</strong> ✓</> : 'שלח לאיש צוות (מדפיס מדבקה + שולח וואטסאפ לצוות)'}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { name: 'ברי',   accent: 'from-purple-500 to-purple-700' },
                    { name: 'לירן',  accent: 'from-teal-500 to-teal-700' },
                    { name: 'יניב',  accent: 'from-indigo-500 to-indigo-700' },
                    { name: 'דניאל', accent: 'from-orange-500 to-orange-700' },
                  ].map(t => (
                    <button
                      key={t.name}
                      onClick={() => assignToTeam(t.name)}
                      disabled={dispatchLoading !== null}
                      className={`py-2.5 rounded-xl bg-gradient-to-br ${t.accent} font-black text-white text-sm transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100 ${assignedTo === t.name ? 'ring-2 ring-white/70' : ''}`}
                      data-testid={`qo-team-${t.name}`}
                    >
                      {dispatchLoading === t.name ? '⏳' : `📦 ${t.name}`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Standalone print */}
              <button
                onClick={printLabel}
                className="w-full py-2.5 rounded-2xl bg-amber-500/80 hover:bg-amber-500 font-black transition-colors text-stone-900 text-sm flex items-center justify-center gap-2"
                data-testid="qo-print-label"
              >
                🖨 הדפס מדבקה בלבד
              </button>
            </div>
            </>)}

            {/* Bottom row inside the card: just the 'הזמנה חדשה' CTA
                for regular customers. The "מערכת" toggle for the operator
                lives OUTSIDE the card as a fixed-bottom bar so it's always
                reachable and clearly separated from the customer view. */}
            <div className="pt-3 border-t border-emerald-500/20">
              <button onClick={reset} className="w-full py-3 rounded-2xl bg-white/10 hover:bg-white/20 font-black transition-colors text-sm" data-testid="qo-new-btn">
                הזמנה חדשה
              </button>
            </div>
          </div>
        )}

        {/* Fixed-bottom "מערכת" bar — only visible after a successful
            order, positioned at the very bottom of the viewport so the
            operator can flip the admin panel from anywhere on the screen
            without hunting for it inside the card. Regular customers can
            ignore it — it's a tiny neutral pill. */}
        {success && (
          <div className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none">
            <div className="max-w-md mx-auto px-4 pb-3 pt-2 bg-gradient-to-t from-stone-950 via-stone-950/80 to-transparent pointer-events-auto">
              <button
                onClick={() => setShowSystemPanel(v => !v)}
                className="w-full py-2.5 rounded-xl bg-stone-800/90 hover:bg-stone-800 text-stone-300 hover:text-white text-xs font-bold transition-colors border border-stone-700 shadow-lg backdrop-blur-sm"
                data-testid="qo-system-toggle"
              >
                {showSystemPanel ? '✕ סגור פאנל מערכת' : '⚙ מערכת'}
              </button>
            </div>
          </div>
        )}

        {/* BIT MANUAL TRANSFER SCREEN */}
        {bitScreen && !bitConfirmed && (
          <div className="bg-gradient-to-br from-cyan-900/50 to-blue-950/70 border border-cyan-500/40 rounded-3xl p-6 backdrop-blur-xl" data-testid="bit-screen">
            <img src={BIT_LOGO} alt="Bit" className="w-20 h-20 mx-auto mb-3 rounded-2xl shadow-lg" />
            <h2 className="text-2xl font-black text-center mb-1">תודה על ההזמנה</h2>
            <p className="text-cyan-200/80 text-center text-sm mb-5">יש להעביר למספר הביט</p>

            <div className="bg-white/10 rounded-2xl p-4 mb-3 flex items-center justify-between">
              <button onClick={() => copy(BIT_PHONE)} className="text-cyan-300 hover:text-cyan-200" title="העתק"><Copy className="w-4 h-4" /></button>
              <p className="text-2xl font-black tracking-wider" dir="ltr">{BIT_PHONE}</p>
            </div>

            <p className="text-amber-200/90 text-sm text-center mb-3 leading-relaxed">לרשום במטרת העברה<br/><strong className="text-amber-300">את השם המלא שלך</strong></p>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 mb-5 text-center">
              <p className="text-[11px] text-amber-200/70 mb-1">סכום להעברה</p>
              <p className="text-4xl font-black text-amber-300" data-testid="bit-amount">{bitScreen.amount.toLocaleString()} ₪</p>
            </div>

            <p className="text-center text-[11px] text-cyan-200/70 mb-5">המערכת תזהה את ההעברה ותקבל אישור שילוח</p>

            <button onClick={() => setBitConfirmed(true)} className="w-full py-4 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-black text-lg transition-all" data-testid="bit-confirm-btn">
              ביצעתי העברה
            </button>
          </div>
        )}

        {/* BIT CONFIRMED — final thank-you */}
        {bitScreen && bitConfirmed && (
          <div className="bg-gradient-to-br from-emerald-900/40 to-emerald-950/60 border border-emerald-500/30 rounded-3xl p-7 text-center backdrop-blur-xl" data-testid="bit-confirmed">
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-3" />
            <h2 className="text-3xl font-black mb-3">תודה!</h2>
            <p className="text-emerald-200/90 text-sm mb-3 leading-relaxed">נציגנו יבדקו את הרכישה<br/>ויחזרו אליך בהקדם<br/><strong>עם אישור סופי</strong></p>
            <p className="text-amber-300 text-sm font-bold mb-6">🚚 {SHIPPING_ETA}</p>
            <button onClick={reset} className="w-full py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-600 font-black transition-colors">הזמנה חדשה</button>
          </div>
        )}

        {/* BANK TRANSFER SCREEN */}
        {bankScreen && !bankConfirmed && (
          <div className="bg-gradient-to-br from-indigo-900/50 to-slate-950/70 border border-indigo-400/30 rounded-3xl p-6 backdrop-blur-xl" data-testid="bank-screen">
            <img src={BANK_INFO.logoUrl} alt="מזרחי טפחות" className="w-24 h-24 mx-auto mb-3 rounded-2xl bg-white p-2 shadow-lg object-contain" />
            <h2 className="text-2xl font-black text-center mb-4">העברה בנקאית</h2>

            <div className="bg-white/10 rounded-2xl p-4 mb-4 space-y-2.5">
              <div className="flex justify-between items-center text-sm"><span className="text-stone-300">בנק</span><strong>{BANK_INFO.name} ({BANK_INFO.bankCode})</strong></div>
              <div className="flex justify-between items-center text-sm"><span className="text-stone-300">סניף</span>
                <button onClick={() => copy(BANK_INFO.branch)} className="flex items-center gap-1 hover:text-indigo-300"><Copy className="w-3 h-3" /><strong>{BANK_INFO.branch}</strong></button>
              </div>
              <div className="flex justify-between items-center text-sm"><span className="text-stone-300">חשבון</span>
                <button onClick={() => copy(BANK_INFO.account)} className="flex items-center gap-1 hover:text-indigo-300"><Copy className="w-3 h-3" /><strong>{BANK_INFO.account}</strong></button>
              </div>
              <div className="flex justify-between items-center text-sm"><span className="text-stone-300">על שם</span><strong>{BANK_INFO.beneficiary}</strong></div>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 mb-4 text-center">
              <p className="text-[11px] text-amber-200/70 mb-1">סכום להעברה</p>
              <p className="text-4xl font-black text-amber-300" data-testid="bank-amount">{bankScreen.amount.toLocaleString()} ₪</p>
            </div>

            <p className="text-center text-sm text-indigo-200/90 mb-1 leading-relaxed">לזרוז המשלוח יש לשלוח<br/><strong>צילום מסך של האסמכתא בוואצאפ</strong></p>
            <p className="text-center text-[11px] text-indigo-200/60 mb-5">תודה מראש</p>

            <button onClick={() => setBankConfirmed(true)} className="w-full py-4 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 hover:from-indigo-600 hover:to-indigo-800 text-white font-black text-lg transition-all" data-testid="bank-confirm-btn">
              ביצעתי את ההעברה
            </button>
          </div>
        )}

        {/* BANK CONFIRMED — same thank-you screen as Bit. */}
        {bankScreen && bankConfirmed && (
          <div className="bg-gradient-to-br from-emerald-900/40 to-emerald-950/60 border border-emerald-500/30 rounded-3xl p-7 text-center backdrop-blur-xl" data-testid="bank-confirmed">
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-3" />
            <h2 className="text-3xl font-black mb-3">תודה!</h2>
            <p className="text-emerald-200/90 text-sm mb-3 leading-relaxed">נציגנו יבדקו את הרכישה<br/>ויחזרו אליך בהקדם<br/><strong>עם אישור סופי</strong></p>
            <p className="text-amber-300 text-sm font-bold mb-6">🚚 {SHIPPING_ETA}</p>
            <button onClick={reset} className="w-full py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-600 font-black transition-colors">הזמנה חדשה</button>
          </div>
        )}

        {/* MAIN FORM */}
        {!success && !bitScreen && !bankScreen && (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* PRODUCTS */}
            <section className="bg-black/30 border border-white/10 rounded-2xl p-4 backdrop-blur-md" data-testid="qo-products">
              <p className="text-amber-300 text-xs font-bold mb-3 tracking-widest">בחירת מוצר</p>
              <div className="space-y-2">
                {products.length === 0 && <p className="text-stone-400 text-sm">טוען מוצרים…</p>}
                {(() => {
                  // Split into "main" (men's) products and "partner"
                  // (women's) — the 2 partner products live in a separate
                  // section titled "פנק את בת הזוג" so the operator sees a
                  // clear boundary between the men's core lineup and the
                  // pair-bonding upsells.
                  const mainProds = products.filter(p => (p.category || '') !== 'partner')
                    .sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99));
                  const partnerProds = products.filter(p => (p.category || '') === 'partner');
                  const renderRow = (p) => {
                    const qty = selected[p.id] || 0;
                    const img = photoOf(p);
                    return (
                      <div key={p.id} className={`flex items-center gap-2 p-2 rounded-xl border transition-all ${qty > 0 ? 'bg-orange-500/10 border-orange-500/40' : 'bg-white/5 border-white/10'}`}>
                        {img ? <img src={img} alt={p.name} className="w-10 h-10 rounded-lg object-cover bg-white/10" /> : <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-amber-400">📦</div>}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold truncate" title={p.name}><bdi>{p.name}</bdi></p>
                          <p className="text-[11px] flex items-center gap-1.5">
                            {p.old_price && p.old_price > p.price && (
                              <span className="text-stone-400 line-through" data-testid={`qo-old-price-${p.id}`}>{p.old_price.toLocaleString()} ₪</span>
                            )}
                            <span className="text-amber-300/90 font-bold" data-testid={`qo-price-${p.id}`}>{p.price.toLocaleString()} ₪</span>
                            {(() => {
                              // Short gift badge left of the price — derived from
                              // gift_text like "90 טבליות + 90 טבליות מתנה".
                              const m = (p.gift_text || '').match(/(\d+)\s*טבליות\s*\+\s*(\d+)/);
                              if (!m) {
                                if (/1\+1/.test(p.gift_text || '')) return <span className="bg-emerald-500/20 text-emerald-300 font-bold px-1.5 py-0.5 rounded" data-testid={`qo-gift-badge-${p.id}`}>1+1 מתנה</span>;
                                return null;
                              }
                              return <span className="bg-emerald-500/20 text-emerald-300 font-bold px-1.5 py-0.5 rounded" data-testid={`qo-gift-badge-${p.id}`}>{m[1]}+{m[2]} מתנה</span>;
                            })()}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => handleProductClick(p, -1)} disabled={qty === 0} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30 font-black" data-testid={`qo-minus-${p.id}`}>−</button>
                          <span className="w-7 text-center font-bold" data-testid={`qo-qty-${p.id}`}>{qty}</span>
                          <button type="button" onClick={() => handleProductClick(p, +1)} className="w-8 h-8 rounded-lg bg-orange-500 hover:bg-orange-600 font-black" data-testid={`qo-plus-${p.id}`}>+</button>
                        </div>
                      </div>
                    );
                  };
                  return (
                    <>
                      {mainProds.map(renderRow)}
                      {partnerProds.length > 0 && (
                        <div className="pt-3 pb-1" data-testid="qo-partner-section-header">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-px bg-gradient-to-l from-pink-400/50 to-transparent" />
                            <p className="text-pink-300 text-xs font-bold tracking-widest">💗 פנק את בת הזוג</p>
                            <div className="flex-1 h-px bg-gradient-to-r from-pink-400/50 to-transparent" />
                          </div>
                        </div>
                      )}
                      {partnerProds.map(renderRow)}
                    </>
                  );
                })()}
              </div>
              {/* Aggregate gift summary — shown when there's at least one
                  item with `gift_text`. Joins all gift lines so admins see
                  the full "bundle" at a glance. Auto-hidden when empty. */}
              {(() => {
                const gifts = items
                  .map(it => {
                    const p = products.find(pr => pr.id === it.product_id);
                    return p?.gift_text ? `${p.gift_text}` : null;
                  })
                  .filter(Boolean);
                if (gifts.length === 0) return null;
                return (
                  <div className="mt-3 bg-amber-500/15 border border-amber-500/40 rounded-xl p-3 flex items-start gap-2" data-testid="qo-gift-summary">
                    <Gift className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
                    <div className="text-amber-200 text-sm font-bold leading-snug">
                      {gifts.map((g, i) => <p key={i}>{g}</p>)}
                    </div>
                  </div>
                );
              })()}
            </section>
            <section className="bg-black/30 border border-white/10 rounded-2xl p-4 backdrop-blur-md" data-testid="qo-payment">
              <p className="text-amber-300 text-xs font-bold mb-3 tracking-widest">סוג תשלום</p>
              <div className="grid grid-cols-2 gap-2">
                {PAYMENT_METHODS.map(m => {
                  const Icon = m.icon;
                  const active = payment === m.id;
                  return (
                    <button type="button" key={m.id} onClick={() => setPayment(m.id)} className={`relative p-3 rounded-xl border-2 transition-all text-right ${active ? `bg-gradient-to-br ${m.accent} border-white/40 shadow-lg` : 'bg-white/5 border-white/10 hover:border-white/30'}`} data-testid={`qo-pay-${m.id}`}>
                      <Icon className="w-5 h-5 mb-1 opacity-90" />
                      <p className="font-bold text-sm">{m.label}</p>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* SHIPPING */}
            <section className="bg-black/30 border border-white/10 rounded-2xl p-4 backdrop-blur-md" data-testid="qo-shipping">
              <p className="text-amber-300 text-xs font-bold mb-3 tracking-widest">סוג משלוח</p>
              <div className="space-y-1.5">
                {effectiveShippingOptions.map(s => {
                  const active = shipping === s.id;
                  const isPickup = s.id === 'pickup_zigzag' || s.id === 'pickup';
                  const free = isPickup && subtotal >= FREE_PICKUP_THRESHOLD;
                  // Tapping a shipping row opens the info popup for that
                  // method (only for delivery & pickup — cash-locked rows
                  // get selected immediately).
                  const onPick = () => {
                    const info = SHIPPING_INFO_POPUPS[s.id];
                    if (info) {
                      setShippingPopup({ id: s.id, ...info, cost: s.cost });
                    } else {
                      setShipping(s.id);
                    }
                  };
                  return (
                    <button type="button" key={s.id} onClick={onPick} className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-right ${active ? 'bg-orange-500/15 border-orange-500/50' : 'bg-white/5 border-white/10 hover:border-white/30'}`} data-testid={`qo-ship-${s.id}`}>
                      <span className="font-bold text-sm">{s.label}</span>
                      <span className="text-xs text-amber-300/80">
                        {free ? (<><span className="line-through opacity-50 ml-1">{s.cost} ₪</span><strong className="text-green-400">חינם</strong></>) : (s.cost ? `${s.cost} ₪` : 'חינם')}
                      </span>
                    </button>
                  );
                })}
              </div>
              {subtotal > 0 && subtotal < FREE_PICKUP_THRESHOLD && (
                <p className="text-[11px] text-amber-200/70 mt-2 text-center">עוד {(FREE_PICKUP_THRESHOLD - subtotal).toLocaleString()} ₪ ותקבל נקודת איסוף חינם</p>
              )}

              {/* HFD pickup-point picker — search by city / name, tap to select */}
              {shipping === 'pickup' && (
                <div className="mt-3 space-y-2" data-testid="qo-hfd-picker">
                  {hfdPoint ? (
                    <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-3" data-testid="qo-hfd-selected">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-emerald-300 truncate">{hfdPoint.spotName}, {hfdPoint.city}</p>
                        <p className="text-[11px] text-stone-400 truncate">{[hfdPoint.street, hfdPoint.house].filter(Boolean).join(' ')}{hfdPoint.kind === 'locker' ? ' · לוקר' : ' · חנות'}</p>
                      </div>
                      <button type="button" onClick={() => { setHfdPoint(null); }} className="text-xs text-amber-300 underline shrink-0 mr-2" data-testid="qo-hfd-change">החלף</button>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2">
                        <input
                          value={hfdSearch}
                          onChange={e => setHfdSearch(e.target.value)}
                          placeholder="עיר *"
                          className="flex-1 min-w-0 bg-white/5 border border-white/15 focus:border-orange-400 rounded-xl px-3 py-2.5 text-sm outline-none placeholder-stone-500"
                          data-testid="qo-hfd-search"
                        />
                        <input
                          value={hfdStreet}
                          onChange={e => setHfdStreet(e.target.value)}
                          placeholder="רחוב (לא חובה)"
                          className="flex-1 min-w-0 bg-white/5 border border-white/15 focus:border-orange-400 rounded-xl px-3 py-2.5 text-sm outline-none placeholder-stone-500"
                          data-testid="qo-hfd-street"
                        />
                      </div>
                      {hfdLoading && <p className="text-xs text-stone-400 text-center py-2">טוען נקודות איסוף...</p>}
                      {!hfdLoading && hfdSearch.trim().length >= 2 && (() => {
                        const q = hfdSearch.trim();
                        const st = hfdStreet.trim();
                        const matches = hfdPoints.filter(pt =>
                          ((pt.city || '').includes(q) || (pt.spotName || '').includes(q)) &&
                          (!st || `${pt.street || ''} ${pt.house || ''}`.includes(st))
                        ).slice(0, 20);
                        if (matches.length === 0) return <p className="text-xs text-stone-400 text-center py-2">לא נמצאו נקודות — נסה עיר או רחוב אחרים</p>;
                        return (
                          <div className="max-h-52 overflow-y-auto space-y-1 pr-0.5" data-testid="qo-hfd-results">
                            {matches.map(pt => (
                              <button
                                type="button"
                                key={pt.spotId}
                                onClick={() => { setHfdPoint(pt); setHfdSearch(''); setHfdStreet(''); }}
                                className="w-full text-right bg-white/5 hover:bg-orange-500/10 border border-white/10 hover:border-orange-500/40 rounded-lg px-3 py-2 transition-all"
                                data-testid={`qo-hfd-point-${pt.spotId}`}
                              >
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ml-2 ${pt.kind === 'locker' ? 'bg-orange-500/30 text-orange-200' : 'bg-blue-500/30 text-blue-200'}`}>{pt.kind === 'locker' ? 'לוקר' : 'חנות'}</span>
                                <span className="text-sm font-bold">{pt.spotName}</span>
                                <span className="block text-[12px] text-amber-200/90 font-bold">{pt.city} · {[pt.street, pt.house].filter(Boolean).join(' ') || 'כתובת תופיע בעת המסירה'}</span>
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                      {!hfdLoading && hfdSearch.trim().length < 2 && hfdPoints.length > 0 && (
                        <p className="text-[11px] text-stone-500 text-center">הקלד עיר (לפחות 2 תווים) לחיפוש מתוך {hfdPoints.length.toLocaleString()} נקודות</p>
                      )}
                    </>
                  )}
                </div>
              )}
            </section>

            {/* CUSTOMER FIELDS */}
            <section className="bg-black/30 border border-white/10 rounded-2xl p-4 backdrop-blur-md space-y-3" data-testid="qo-customer">
              <p className="text-amber-300 text-xs font-bold tracking-widest">פרטי לקוח</p>
              <input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} placeholder="שם מלא *" className="w-full bg-white/5 border border-white/15 focus:border-orange-400 rounded-xl px-3 py-3 text-base outline-none placeholder-stone-500" autoComplete="name" data-testid="qo-name" />
              <input value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} placeholder="טלפון *" type="tel" dir="ltr" className="w-full bg-white/5 border border-white/15 focus:border-orange-400 rounded-xl px-3 py-3 text-base outline-none placeholder-stone-500 text-right" autoComplete="tel" data-testid="qo-phone" />
              {/* Address as single horizontal row: street | number | city.
                  Flex with proportional widths so it fits even on small phones. */}
              {/* Address row hidden for HFD pickup — the point's address is used */}
              {shipping !== 'pickup' && (
              <div className="flex gap-2" data-testid="qo-address-row">
                <input value={form.street} onChange={e => setForm({ ...form, street: e.target.value })} placeholder="רחוב *" className="flex-1 min-w-0 bg-white/5 border border-white/15 focus:border-orange-400 rounded-xl px-3 py-3 text-base outline-none placeholder-stone-500" data-testid="qo-street" />
                <input value={form.house_number} onChange={e => setForm({ ...form, house_number: e.target.value })} placeholder="מס׳" className="w-20 bg-white/5 border border-white/15 focus:border-orange-400 rounded-xl px-3 py-3 text-base outline-none placeholder-stone-500 text-center" data-testid="qo-house" />
                <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="עיר *" className="flex-1 min-w-0 bg-white/5 border border-white/15 focus:border-orange-400 rounded-xl px-3 py-3 text-base outline-none placeholder-stone-500" data-testid="qo-city" />
              </div>
              )}
              <input value={coupon} onChange={e => setCoupon(e.target.value)} placeholder="קופון (לא חובה)" className="w-full bg-amber-500/5 border border-amber-500/20 focus:border-amber-400 rounded-xl px-3 py-3 text-base outline-none placeholder-amber-300/40 text-amber-200" data-testid="qo-coupon" />
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="הערות (לא חובה)" rows={2} className="w-full bg-white/5 border border-white/15 focus:border-orange-400 rounded-xl px-3 py-3 text-sm outline-none placeholder-stone-500 resize-none" data-testid="qo-notes" />
            </section>

            {/* SUMMARY */}
            <section className="bg-gradient-to-br from-amber-500/10 to-orange-700/10 border border-amber-500/30 rounded-2xl p-4">
              <div className="flex justify-between text-sm"><span className="text-stone-300">מוצרים</span><strong>{subtotal.toLocaleString()} ₪</strong></div>
              {couponDiscount > 0 && (
                <div className="flex justify-between text-sm mt-1 text-emerald-300" data-testid="qo-coupon-line">
                  <span>קופון {coupon}</span><strong>−{couponDiscount.toLocaleString()} ₪</strong>
                </div>
              )}
              <div className="flex justify-between text-sm mt-1">
                <span className="text-stone-300">משלוח</span>
                <strong>{pickupFree ? <><span className="line-through opacity-50 ml-1">{rawShipCost} ₪</span><span className="text-green-400">חינם</span></> : `${shipCost.toLocaleString()} ₪`}</strong>
              </div>
              <div className="flex justify-between items-baseline mt-2 pt-2 border-t border-amber-500/30">
                <span className="text-lg font-black text-amber-200">סה״כ</span>
                <span className="text-3xl font-black text-amber-300" data-testid="qo-total">{total.toLocaleString()} ₪</span>
              </div>
            </section>

            <button type="submit" disabled={!canSubmit} className="w-full py-4 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black text-lg tracking-wider shadow-xl shadow-orange-700/30 transition-all flex items-center justify-center gap-2" data-testid="qo-submit">
              {submitting ? <Loader2 className="w-6 h-6 animate-spin" /> : <Zap className="w-6 h-6" />}
              {payment === 'credit_card' ? 'המשך לתשלום אשראי' : payment === 'bit' ? 'המשך ל-Bit' : payment === 'bank_transfer' ? 'המשך להעברה בנקאית' : 'בצע הזמנה'}
            </button>
          </form>
        )}

        <p className="text-center text-[10px] text-stone-500 mt-8">בריאות הגבר · תשלום מהיר · v2</p>
      </div>

      {/* PRODUCT CONFIRMATION POPUP — shows on every "+" click so admin
          confirms they picked the right product. Bigger image, full name,
          gift banner (90+90) if applicable. Backdrop-tap closes. */}
      {productPopup && (
        <div onClick={() => setProductPopup(null)} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in" data-testid="product-popup">
          <div onClick={e => e.stopPropagation()} className="bg-gradient-to-br from-stone-900 to-black border border-amber-500/30 rounded-3xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex justify-end mb-2">
              <button onClick={() => setProductPopup(null)} className="text-stone-400 hover:text-white" data-testid="product-popup-close"><X className="w-5 h-5" /></button>
            </div>
            {photoOf(productPopup) ? (
              <img src={photoOf(productPopup)} alt={productPopup.name} className="w-44 h-44 mx-auto rounded-2xl object-contain mb-4 bg-white" />
            ) : (
              <div className="w-44 h-44 mx-auto rounded-2xl bg-white/5 flex items-center justify-center mb-4 text-amber-400 text-4xl">📦</div>
            )}
            <h3 className="text-xl font-black text-center mb-2">{productPopup.name}</h3>
            <p className="text-center mb-3 flex items-center justify-center gap-2">
              {productPopup.old_price && productPopup.old_price > productPopup.price && (
                <span className="text-lg text-stone-400 line-through">{productPopup.old_price.toLocaleString()} ₪</span>
              )}
              <span className="text-2xl font-black text-amber-300">{productPopup.price.toLocaleString()} ₪</span>
            </p>
            {productPopup.gift_text && (
              <div className="bg-amber-500/15 border border-amber-500/40 rounded-2xl p-3 mb-3 flex items-center gap-2">
                <Gift className="w-5 h-5 text-amber-300 flex-shrink-0" />
                <p className="text-amber-200 text-sm font-bold">{productPopup.gift_text}</p>
              </div>
            )}
            {productPopup.description && (
              <p className="text-stone-300 text-sm text-center mb-4 leading-relaxed whitespace-pre-line">{productPopup.description}</p>
            )}
            <button onClick={confirmAddProduct} className="w-full py-3 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 font-black transition-all" data-testid="product-popup-confirm">
              הוסף לעגלה
            </button>
          </div>
        </div>
      )}
      {/* SHIPPING INFO POPUP — confirms timing/location info before
          the user commits to a shipping option. "המשך" finalises the
          selection; backdrop-tap or X cancels. */}
      {shippingPopup && (
        <div onClick={() => setShippingPopup(null)} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" data-testid="shipping-info-popup">
          <div onClick={e => e.stopPropagation()} className="bg-gradient-to-br from-stone-900 to-black border border-amber-500/30 rounded-3xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex justify-end mb-1">
              <button onClick={() => setShippingPopup(null)} className="text-stone-400 hover:text-white" data-testid="shipping-info-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="w-20 h-20 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center shadow-lg">
              {shippingPopup.icon === 'home' ? <Home className="w-10 h-10 text-white" /> : <MapPin className="w-10 h-10 text-white" />}
            </div>
            <h3 className="text-xl font-black text-center mb-3">{shippingPopup.title}</h3>
            <p className="text-stone-300 text-sm text-center mb-5 leading-relaxed">{shippingPopup.body}</p>
            <button
              onClick={() => { setShipping(shippingPopup.id); setShippingPopup(null); }}
              className="w-full py-3 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 font-black transition-all flex items-center justify-center gap-1"
              data-testid="shipping-info-continue"
            >
              המשך <ChevronLeft className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickOrder;
